// src/pages/Dashboard.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import api from "../utils/api";

import bgImg from "../images/dashboard-bg.jpg";

const Dashboard = () => {
  const [seller, setSeller] = useState(null);
  const [menu, setMenu] = useState([]);

// after: const [seller, setSeller] = useState(null);
const STATUS_OK = new Set(["registered", "approved", "active", "live"]);
const canManageMenu = !!seller && STATUS_OK.has(String(seller.council_status || "").toLowerCase());
const isPending = !!seller && !canManageMenu;



  // ----- Add Dish -----
  const [form, setForm] = useState({
    dish_name: "",
    description: "",
    price: "",
    meal_time: "breakfast",
    quantity_total: "",
  });
  const [message, setMessage] = useState("");
  const [photo, setPhoto] = useState(null);
  const fileRef = useRef(null);
  const [menuDate, setMenuDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );

  // ----- Edit Dish -----
  const [editDishId, setEditDishId] = useState(null);
  const [editForm, setEditForm] = useState({
    dish_name: "",
    description: "",
    price: "",
    meal_time: "breakfast",
    quantity_total: "",
  });
  const [editPhoto, setEditPhoto] = useState(null);

  // ----- Profile Photo -----
  const [pfFile, setPfFile] = useState(null);
  const [pfPreview, setPfPreview] = useState(null);
  const [pfErr, setPfErr] = useState("");
  const [pfUploading, setPfUploading] = useState(false);
  const pfRef = useRef(null);

  const validateProfilePhoto = (file) => {
    if (!file) return "Select a file.";
    const ok = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
    if (!ok) return "Use JPG, PNG, or WebP.";
    if (file.size > 2 * 1024 * 1024) return "Max size is 2MB.";
    return "";
  };

  const handlePfChange = (e) => {
    const f = e.target.files?.[0] || null;
    setPfFile(f);
    const err = f ? validateProfilePhoto(f) : "";
    setPfErr(err);
    setPfPreview(f ? URL.createObjectURL(f) : null);
  };

  const uploadProfilePhoto = async () => {
    if (!pfFile) return;
    const err = validateProfilePhoto(pfFile);
    if (err) {
      setPfErr(err);
      return;
    }
    setPfUploading(true);
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("photo", pfFile);
      const res = await api.post("/auth/upload-photo", fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setSeller((s) => ({ ...s, photo_url: res.data.photo_url }));
      setMessage("✅ Profile photo updated.");
      setPfFile(null);
      setPfPreview(null);
      setPfErr("");
      if (pfRef.current) pfRef.current.value = "";
      setTimeout(() => setMessage(""), 1600);
    } catch (err) {
      console.error("Profile upload failed", err);
      setMessage(err?.response?.data?.error || "❌ Failed to upload photo");
      setTimeout(() => setMessage(""), 2000);
    } finally {
      setPfUploading(false);
    }
  };

  const cancelProfilePhoto = () => {
    setPfFile(null);
    setPfPreview(null);
    setPfErr("");
    if (pfRef.current) pfRef.current.value = "";
  };

  // --- Dish validation state ---
  const [dishErr, setDishErr] = useState({});
  const [dishTouched, setDishTouched] = useState({});
  const markTouched = (name) =>
    setDishTouched((t) => ({ ...t, [name]: true }));
  const validateDish = (data, photoFile) => {
    const e = {};
    if (!data.dish_name?.trim()) e.dish_name = "Dish name is required.";
    if (!String(data.price ?? "").trim()) e.price = "Price is required.";
    else if (!/^\d+(\.\d{1,2})?$/.test(String(data.price)))
      e.price = "Enter a valid price (e.g., 6.50).";
    if (!data.meal_time) e.meal_time = "Select a meal time.";
    if (String(data.quantity_total ?? "").trim() === "") {
      e.quantity_total = "Quantity is required.";
    } else if (!/^\d+$/.test(String(data.quantity_total))) {
      e.quantity_total = "Quantity must be a whole number ≥ 0.";
    }
    if (photoFile) {
      const okType = ["image/jpeg", "image/png", "image/webp"].includes(
        photoFile.type
      );
      if (!okType) e.photo = "Use JPG, PNG, or WebP.";
      if (photoFile.size > 5 * 1024 * 1024)
        e.photo = "Image must be 5MB or less.";
    }
    setDishErr(e);
    return Object.keys(e).length === 0;
  };

  const navigate = useNavigate();

  const handleLogout = () => {
    try {
      localStorage.removeItem("token");
    } catch {}
    setSeller(null);
    navigate("/seller/login", { replace: true });
  };

  // axios interceptor → logout on 401
  const axiosInterceptorId = useRef(null);
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) handleLogout();
        return Promise.reject(err);
      }
    );
    axiosInterceptorId.current = id;
    return () => {
      if (axiosInterceptorId.current !== null) {
        axios.interceptors.response.eject(axiosInterceptorId.current);
        axiosInterceptorId.current = null;
      }
    };
  }, []);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) return navigate("/seller/login");
    fetchDashboard();
    fetchMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuDate]);

  const fetchDashboard = async () => {
    try {
      const res = await api.get("/auth/dashboard", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSeller(res.data.seller);
    } catch (err) {
      console.error("Dashboard fetch failed", err);
      navigate("/login");
    }
  };

  const fetchMenu = async () => {
    try {
      const res = await api.get(`/auth/menu?date=${menuDate}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMenu(res.data.menu);
    } catch (err) {
      console.error("Menu fetch failed", err);
    }
  };

  const handleEditClick = (dish) => {
    setEditDishId(dish.menu_id);
    setEditForm({
      dish_name: dish.dish_name,
      description: dish.description,
      price: dish.price,
      meal_time: dish.meal_time,
      quantity_total: dish.quantity_total ?? 0,
      seller_can_deliver:
      dish.seller_can_deliver === true ||
      dish.seller_can_deliver === "t" ||
      dish.seller_can_deliver === "true",
	
    });
    setEditPhoto(null);
  };

const handleUpdateDish = async (e) => {
  e.preventDefault();
  const token = localStorage.getItem("token");
  const formData = new FormData();
  formData.append("dish_name", editForm.dish_name);
  formData.append("description", editForm.description);
  formData.append("price", editForm.price);
  formData.append("meal_time", editForm.meal_time);
  if (editForm.quantity_total !== "") {
    formData.append("quantity_total", String(editForm.quantity_total));
  }
  if (editForm.seller_can_deliver !== undefined) {
  formData.append("seller_can_deliver", editForm.seller_can_deliver ? "true" : "false");
  }
  if (editPhoto) formData.append("photo", editPhoto);

  try {
    await api.put(`/auth/menu/${editDishId}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    setEditDishId(null);
    fetchMenu();
    setMessage("✅ Dish updated.");
  } catch (err) {
    console.error("Update dish failed", err);
    setMessage("❌ Failed to update dish");
  }
};

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleAddDish = async (e) => {
    e.preventDefault();
    setMessage("");
    if (!validateDish(form, photo)) {
      setDishTouched({
        dish_name: true,
        price: true,
        meal_time: true,
        photo: true,
        quantity_total: true,
      });
      return;
    }
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("dish_name", form.dish_name.trim());
    formData.append("description", form.description?.trim() || "");
    formData.append("price", String(form.price));
    formData.append("meal_time", form.meal_time);
    formData.append("quantity_total", String(form.quantity_total ?? "0"));
    formData.append("available_date", menuDate);
    formData.append("seller_can_deliver", form.seller_can_deliver ? "true" : "false"); // ✅ added
    if (photo) formData.append("photo", photo);

    try {
      await api.post("/auth/menu-with-photo", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setForm({
        dish_name: "",
        description: "",
        price: "",
        meal_time: "breakfast",
        quantity_total: "",
      });
      setPhoto(null);
      if (fileRef.current) fileRef.current.value = "";
      setDishErr({});
      setDishTouched({});
      fetchMenu();
      setMessage("✅ Dish added.");
      setTimeout(() => setMessage(""), 1800);
    } catch (err) {
      console.error("❌ Add Dish with Photo Error:", err);
      setMessage(err?.response?.data?.error || "❌ Failed to add dish");
    }
  };

  const handleDeleteDish = async (menu_id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this dish?");
    if (!confirmDelete) return;
    try {
      await api.delete(`/auth/menu/${menu_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchMenu();
      setMessage("🗑️ Dish deleted.");
      setTimeout(() => setMessage(""), 1500);
    } catch (err) {
      console.error("Delete dish failed", err);
      setMessage("❌ Failed to delete dish");
    }
  };

  const groupByMealTime = (dishes) =>
    dishes.reduce((acc, d) => {
      acc[d.meal_time] = acc[d.meal_time] || [];
      acc[d.meal_time].push(d);
      return acc;
    }, {});

  const mealCounts = useMemo(() => {
    const counts = { breakfast: 0, lunch: 0, dinner: 0 };
    menu.forEach(
      (m) => (counts[m.meal_time] = (counts[m.meal_time] || 0) + 1)
    );
    return counts;
  }, [menu]);

  const stockClass = (remaining) =>
    remaining <= 0 ? "stock soldout" : remaining <= 2 ? "stock low" : "stock ok";

  if (!seller) return <p className="center">Loading…</p>;

  const groupedMenu = groupByMealTime(menu);
  const order = ["breakfast", "lunch", "dinner"];
  const previewUrl = photo ? URL.createObjectURL(photo) : null;

const adjustSold = async (menu_id, type = "sell", qty = 1) => {
  try {
    const url =
      type === "sell"
        ? `/auth/menu/${menu_id}/sell`
        : `/auth/menu/${menu_id}/restock`;

    await api.patch(
      url,
      { qty },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    fetchMenu();
  } catch (err) {
    console.error("Adjust sold failed", err);
    setMessage(err?.response?.data?.error || "❌ Could not update stock");
    setTimeout(() => setMessage(""), 1800);
  }
};








return (
  <div
    className="dashboard-page"
    style={{
      backgroundImage: `url(${bgImg})`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center center",
      backgroundAttachment: "fixed",
      backgroundSize: "cover",
      minHeight: "100vh",
    }}
  >
    <div className="dashboard-overlay" />
    <div className="dashboard-content">
      <div className="dash-wrap">
        {/* 🔔 Pending banner (not publicly listed) */}
        {isPending && (
          <div
            className="toast"
            style={{
              background: "#FFF8E1",
              color: "#7A4F01",
              marginBottom: "1rem",
              padding: "10px 12px",
              borderRadius: "8px",
            }}
          >
            <b>Registration not complete.</b> You can’t list dishes yet.
            Finish your council registration and get verified—use the button below to let us handle it for you.
            {seller?.coverage_label && (
              <div>
                <small>Area: {seller.coverage_label}</small>
              </div>
            )}
          </div>
        )}

        {/* Profile card */}
        <section className="profile-card">
          <div className="pfp">
            {seller.photo_url ? (
		
		<img
  		src={
    		seller.photo_url?.startsWith("http")
      		? `${seller.photo_url}?v=${Date.now()}`
      		: `${process.env.REACT_APP_API_URL || ""}${seller.photo_url}?v=${Date.now()}`
 		 }
  		alt="Profile"
		/>


            ) : (
              <div className="pfp-fallback">🍽️</div>
            )}
          </div>
          <div className="details">
            <div className="details-top">
              <h2>👋 Welcome, {seller.name}</h2>

              {/* Actions */}
              <div className="pfp-actions">
                <input
                  ref={pfRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePfChange}
                  style={{ display: "none" }}
                />
                <button
                  className="btn small"
                  type="button"
                  onClick={() => pfRef.current?.click()}
                >
                  Change photo
                </button>

                {pfPreview && (
                  <span className="pfp-inline-upload">
                    <img className="thumb" src={pfPreview} alt="Preview" />
                    <button
                      className="btn small"
                      type="button"
                      onClick={uploadProfilePhoto}
                      disabled={pfUploading || !!pfErr}
                    >
                      {pfUploading ? "Uploading..." : "Save"}
                    </button>
                    <button
                      className="btn ghost small"
                      type="button"
                      onClick={cancelProfilePhoto}
                    >
                      Cancel
                    </button>
                  </span>
                )}

                {/* 🔑 Registration CTA — always visible for pending sellers */}
		
		{/* 🔑 Registration CTA — pending sellers only */}
{isPending && (
  <button
    className="btn small"
    type="button"
    onClick={() => navigate("/seller/apply")}
    style={{ marginLeft: ".25rem" }}
  >
    Start Registration
  </button>
)}

                {/* Logout */}
                <button
                  className="btn danger small"
                  type="button"
                  onClick={handleLogout}
                  style={{ marginLeft: ".25rem" }}
                >
                  Logout
                </button>
              </div>
              {pfErr && <small className="error">{pfErr}</small>}
            </div>

            {/* Details grid */}
	{/* Details grid */}
<div className="grid meta">
  <div><b>Email:</b> {seller.email}</div>
  <div><b>Phone:</b> {seller.phone}</div>
  <div><b>Postcode:</b> {seller.postcode}</div>

  <div>
    <b>Cuisine:</b>{" "}
    {seller.cuisine_types?.length
      ? seller.cuisine_types.join(", ")
      : "—"}
  </div>

  <div>
    <b>Price Category:</b>{" "}
    {seller.price_categories?.length
      ? seller.price_categories.join(", ")
      : "—"}
  </div>

  <div>
    <b>Meal Types:</b>{" "}
    {seller.meal_types?.length
      ? seller.meal_types.join(", ")
      : "—"}
  </div>

  <div className="span-2">
    <b>Council Registration Status:</b> {seller.council_status || "pending"}
  </div>


            </div>
          </div>
        </section>

        {/* Stats & date picker only for registered sellers */}
        {canManageMenu && (
          <>
            <div className="stats">
              <div className="chip">🥞 Breakfast: {mealCounts.breakfast || 0}</div>
              <div className="chip">🥗 Lunch: {mealCounts.lunch || 0}</div>
              <div className="chip">🍛 Dinner: {mealCounts.dinner || 0}</div>
            </div>

            <div className="row menu-date">
              <label className="lbl">Menu date:</label>
              <input
                type="date"
                value={menuDate}
                onChange={(e) => setMenuDate(e.target.value)}
              />
            </div>

            {message && <div className="toast">{message}</div>}

            {/* Add dish form */}
            <section className="card">
              <h3>➕ Add Dish to Today’s Menu</h3>
              <form className="add-form" onSubmit={handleAddDish} noValidate>
                <label>
                  <input
                    name="dish_name"
                    placeholder="Dish Name"
                    value={form.dish_name}
                    onChange={handleFormChange}
                    onBlur={() => markTouched("dish_name")}
                    required
                  />
                  {dishTouched.dish_name && dishErr.dish_name && (
                    <small className="error">{dishErr.dish_name}</small>
                  )}
                </label>

                <label>
                  <input
                    name="description"
                    placeholder="Description"
                    value={form.description}
                    onChange={handleFormChange}
                    maxLength={200}
                  />
                  <small className="hint">Optional — 200 characters max.</small>
                </label>

                <label>
                  <input
                    name="price"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    placeholder="Price"
                    value={form.price}
                    onChange={handleFormChange}
                    onBlur={() => markTouched("price")}
                    required
                  />
                  {dishTouched.price && dishErr.price && (
                    <small className="error">{dishErr.price}</small>
                  )}
                </label>

                <label>
                  <input
                    name="quantity_total"
                    type="number"
                    step="1"
                    inputMode="numeric"
                    placeholder="Quantity"
                    value={form.quantity_total}
                    onChange={handleFormChange}
                    onBlur={() => markTouched("quantity_total")}
                    required
                  />
                  {dishTouched.quantity_total && dishErr.quantity_total && (
                    <small className="error">{dishErr.quantity_total}</small>
                  )}
                </label>
		
		<div className="checkbox-row" style={{ marginTop: "0.5rem" }}>
  <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
    <input
      type="checkbox"
      name="seller_can_deliver"
      checked={!!form.seller_can_deliver}
      onChange={(e) =>
        setForm((f) => ({
          ...f,
          seller_can_deliver: e.target.checked,
        }))
      }
    />
    I can deliver this dish myself 🚗
  </label>
</div>




                <div className="file-row">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setPhoto(f);
                      markTouched("photo");
                      validateDish(form, f);
                    }}
                  />
                  {previewUrl && <img className="thumb" src={previewUrl} alt="Preview" />}
                </div>
                {dishTouched.photo && dishErr.photo && (
                  <small className="error span-2">{dishErr.photo}</small>
                )}

                <label>
                  <select
                    name="meal_time"
                    value={form.meal_time}
                    onChange={handleFormChange}
                    onBlur={() => markTouched("meal_time")}
                    required
                  >
                    <option value="breakfast">Breakfast</option>
                    <option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option>
                  </select>
                  {dishTouched.meal_time && dishErr.meal_time && (
                    <small className="error">{dishErr.meal_time}</small>
                  )}
                </label>

                <button className="btn" type="submit">Add Dish</button>
              </form>
            </section>

            {/* Menu list */}
            <section className="card">
              <h3>📋 Menu for {menuDate}</h3>
              {menu.length === 0 && (
                <p className="muted">No dishes yet for this date. Add your first dish above.</p>
              )}

              {order
                .filter((m) => groupedMenu[m]?.length)
                .map((meal) => (
                  <div className="meal-group" key={meal}>
                    <h4 className="meal-title">
                      {meal === "breakfast" ? "BREAKFAST" : meal === "lunch" ? "LUNCH" : "DINNER"}
                    </h4>

                    <ul className="dish-list">
                      {groupedMenu[meal].map((dish) => {
                        const remaining =
                          dish.quantity_remaining ??
                          (dish.quantity_total - (dish.quantity_sold ?? 0));
                        return (
                          <li key={dish.menu_id} className="dish-card">
                            {editDishId === dish.menu_id ? (
                              <form className="edit-form" onSubmit={handleUpdateDish}>
                                <input
                                  name="dish_name"
                                  value={editForm.dish_name}
                                  onChange={(e) =>
                                    setEditForm({ ...editForm, dish_name: e.target.value })
                                  }
                                />
                                <input
                                  name="description"
                                  value={editForm.description}
                                  onChange={(e) =>
                                    setEditForm({ ...editForm, description: e.target.value })
                                  }
                                />
                                <input
                                  name="price"
                                  type="number"
                                  step="0.01"
                                  value={editForm.price}
                                  onChange={(e) =>
                                    setEditForm({ ...editForm, price: e.target.value })
                                  }
                                />
                                <input
                                  name="quantity_total"
                                  type="number"
                                  step="1"
                                  value={editForm.quantity_total}
                                  onChange={(e) =>
                                    setEditForm({ ...editForm, quantity_total: e.target.value })
                                  }
                                />
                                <select
                                  value={editForm.meal_time}
                                  onChange={(e) =>
                                    setEditForm({ ...editForm, meal_time: e.target.value })
                                  }
                                >
                                  <option value="breakfast">Breakfast</option>
                                  <option value="lunch">Lunch</option>
                                  <option value="dinner">Dinner</option>
                                </select>


        {/* ✅ New seller_can_deliver checkbox */}
        <div className="checkbox-row" style={{ marginTop: "0.5rem" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <input
              type="checkbox"
              name="seller_can_deliver"
              checked={!!editForm.seller_can_deliver}
              onChange={(e) =>
                setEditForm((prev) => ({
                  ...prev,
                  seller_can_deliver: e.target.checked,
                }))
              }
            />
            I can deliver this dish myself 🚗
          </label>
        </div>



                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => setEditPhoto(e.target.files[0])}
                                />
                                <div className="actions">
                                  <button className="btn small" type="submit">Save</button>
                                  <button
                                    className="btn ghost small"
                                    type="button"
                                    onClick={() => setEditDishId(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            ) : (
                              <div className="dish-row">

<div className="dish-media">
  {dish.photo_url && (
    <img
      src={
        dish.photo_url.startsWith("http")
          ? `${dish.photo_url}?v=${encodeURIComponent(dish.updated_at || dish.menu_id)}`
          : `${process.env.REACT_APP_API_URL || ""}${dish.photo_url}?v=${encodeURIComponent(
              dish.updated_at || dish.menu_id
            )}`
      }
      alt="Dish"
      className="dish-img"
    />
  )}
</div>

                                <div className="dish-info">
                                  <div className="dish-top">
                                    <strong className="dish-name">{dish.dish_name}</strong>
                                    <span className="price">£{Number(dish.price).toFixed(2)}</span>
                                  </div>
                                  <div className="desc">{dish.description}</div>

                                  <div className="stock-row">
                                    <span className={stockClass(remaining)}>
                                      In stock: {Math.max(0, remaining)} / Total:{" "}
                                      {dish.quantity_total ?? 0}
                                    </span>
                                    <div className="stock-actions">
                                      <button
                                        className="btn ghost small"
                                        onClick={() => adjustSold(dish.menu_id, "restock", 1)}
                                        title="Decrease sold (restock 1)"
                                      >
                                        –1
                                      </button>
                                      <button
                                        className="btn ghost small"
                                        onClick={() => adjustSold(dish.menu_id, "sell", 1)}
                                        title="Mark 1 sold"
                                        disabled={(remaining ?? 0) <= 0}
                                      >
                                        +1 sold
                                      </button>
                                    </div>
                                  </div>

                                  <div className="actions">
                                    <button
                                      className="btn small"
                                      onClick={() => handleEditClick(dish)}
                                    >
                                      ✏️ Edit
                                    </button>
                                    <button
                                      className="btn danger small"
                                      onClick={() => handleDeleteDish(dish.menu_id)}
                                    >
                                      ❌ Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
            </section>
          </>
        )}

        {/* If pending: big CTA card instead of menu UI */}
        {isPending && (
          <section className="card" style={{ textAlign: "center" }}>
            <h3>Let’s get you registered 🎯</h3>
            <p className="muted" style={{ marginBottom: "1rem" }}>
              We’ll handle the council paperwork so you can start selling fast.
            </p>
            <button className="btn" onClick={() => navigate("/seller/apply")}>
              Start registration
            </button>
          </section>
        )}
      </div>
    </div>
  </div>
);





};  
    
export default Dashboard;
